/* src/views/logements/FicheSavoirCtrl.jsx — L'ONGLET « Ce que l'IA sait »
   de la fiche logement (lot 4, 2026-08-28) : trois vues du proto (Ce qu'il
   sait · Quand il le dit · Lui apprendre) + le banc d'essai toujours
   visible en colonne droite.

   DÉCISION 4 (Dimitri) : le bloc « Pilotage » — LES DEUX VERROUS — vit en
   tête de « Quand il le dit ». Garde-fous de sécurité avant l'esthétique :
   éligibilité (optimiste + rollback) ≠ envoi réel (JAMAIS optimiste,
   modale bloquante + jeton serveur ENVOI_REEL, pastille à trois états,
   texte honnête selon le verrou de déploiement MESSAGES_DRY_RUN — que
   l'écran ne peut jamais lever).

   Les handlers sont PORTÉS FIDÈLEMENT de LogementIATab (mêmes actions,
   mêmes gardes fail-closed). RÈGLES DE DIVULGATION : la table existe
   désormais (`divulgation_regles`) et cet écran la pilote — mais elle NE
   PEUT QUE RESTREINDRE : le socle du moteur reste un PLAFOND non
   franchissable (décision Dimitri 2026-09-06, ET strict dans
   lib/divulgation-resolve.js). Le socle affiché est DÉRIVÉ du moteur, jamais
   recopié à la main : c'est ainsi que l'ancienne liste avait divergé (elle
   annonçait l'adresse « dès le séjour confirmé » alors que le code exige
   aussi la caution). Scope lc1/lc2/lc3 : l'onglet est filtré en amont
   (garde dure serveur inchangée). */

import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../supabase'
import { reasonLabelFR } from '../../lib/inbox-escalation-labels'
import { diagToSentence, modelLabelFR } from '../../lib/inbox-ia-diag-label'
import Feuille from '../accueil/Feuille'
import { ToggleRond } from './proto/LivretProto'

async function callApi(action, body) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  const r = await fetch(`/api/beds24-mapping?action=${action}`, {
    method: 'POST',
    headers: token
      ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      : { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...body }),
  })
  const json = await r.json().catch(() => ({ ok: false, error: 'invalid_json_response' }))
  return { ok: r.ok && json.ok !== false, status: r.status, json }
}

const noteStyle = { fontSize: '10.5px', color: 'var(--gris)', lineHeight: '1.5',
  margin: '8px 0 0' }

// ── DIVULGATION — libellés et phrases DÉRIVÉS du moteur ─────────────────────
// Les 5 champs pilotables (liste blanche fermée, miroir de lib/divulgation-
// resolve.js CHAMPS et du CHECK SQL). Le libellé est cosmétique ; la RÈGLE, on
// ne la recopie jamais — elle vient de `plafond`, rendu par le serveur.
const CHAMPS_DIVULGATION = [
  { champ: 'box_code', lb: 'Le code de la boîte à clés' },
  { champ: 'door_code', lb: 'Le code de la porte' },
  { champ: 'adresse_complete', lb: "L'adresse exacte" },
  { champ: 'instructions_acces_batiment', lb: "Les instructions d'accès au bâtiment" },
  { champ: 'wifi_code', lb: 'Le mot de passe wifi' },
]
const RESTRICTIONS = [
  { cle: 'caution', col: 'exige_caution_recue', lb: 'la caution', body: 'exige_caution_recue' },
  { cle: 'active', col: 'exige_resa_active', lb: 'une résa active', body: 'exige_resa_active' },
  { cle: 'sejour', col: 'exige_sejour_paye', lb: 'le séjour payé', body: 'exige_sejour_paye' },
]
/** La phrase du socle, construite depuis le plafond du moteur. */
function phraseSocle(p) {
  const t = []
  if (p.sejour) t.push('séjour payé')
  if (p.caution) t.push('caution reçue')
  if (p.active) t.push('réservation active')
  return t.length ? t.join(' ET ') : 'dès la réservation'
}
/** Ce que la règle exige EN PLUS du socle (le socle, lui, est indéplaçable). */
function exigencesEnPlus(p, regle) {
  if (!p || !regle) return []
  const out = []
  if (regle.exige_sejour_paye && !p.sejour) out.push('séjour payé')
  if (regle.exige_caution_recue && !p.caution) out.push('caution reçue')
  if (regle.exige_resa_active && !p.active) out.push('réservation active')
  return out
}
const btnRestriction = { border: '1px solid #d5e3ee', background: '#fff',
  borderRadius: '999px', padding: '4px 9px', fontSize: '10px', cursor: 'pointer',
  color: 'var(--navy-d)', fontFamily: 'inherit' }
const btnRestrictionOn = { borderColor: '#c88f7d', background: '#fbf1ed',
  color: '#8a3b28', fontWeight: 600 }
const inpSavoir = { width: '100%', border: '1px solid #d5e3ee', borderRadius: '10px',
  padding: '10px', fontSize: '11.5px', color: 'var(--navy-d)', lineHeight: '1.6',
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' }

/* Les clés techniques du contexte, en français (constat Dimitri 2026-08-28 :
   la feuille montrait box_code / wifi_code bruts). Clé inconnue = affichée
   telle quelle, jamais masquée. */
const LIBELLES_ACCES = {
  box_code: 'Boîte à clés',
  door_code: "Porte de l'immeuble",
  interphone_code: 'Interphone',
  coffre_code: 'Coffre',
  garage_code: 'Garage',
  wifi_name: 'Réseau wifi',
  wifi_code: 'Mot de passe wifi',
  adresse_complete: 'Adresse exacte',
  instructions_acces_batiment: 'Accès au bâtiment',
  instructions_acces_appartement: "Accès à l'appartement",
}

/* Masque les valeurs sensibles (vue par défaut de la feuille Révéler). */
function masquer(v) {
  return v ? '••••••' : v
}

export default function FicheSavoirCtrl({ draft }) {
  const { selected } = draft
  const log = selected.entity
  const logementId = log.id

  // ── RÈGLES DE DIVULGATION (cadrage CADRAGE_DIVULGATION_EN_TABLE.md) ────────
  // `divulg.plafond` vient DU MOTEUR (jamais d'une liste recopiée). `regles`
  // à null = lecture en panne → on affiche le socle et on le DIT, jamais un
  // faux « aucune règle ».
  const [divulg, setDivulg] = useState(null)
  const [divulgBusy, setDivulgBusy] = useState(null)
  const [divulgMsg, setDivulgMsg] = useState('')

  const chargerDivulgation = useCallback(async () => {
    if (!logementId) return
    const { ok, json } = await callApi('divulgation_regles_list', { logementId })
    if (ok) setDivulg(json)
    else setDivulg({ lecture_en_panne: true, plafond: null, regles: null })
  }, [logementId])
  useEffect(() => { chargerDivulgation() }, [chargerDivulgation])

  /** Pose ou retire UNE exigence. Ne peut que restreindre — le serveur le
   *  re-tient de toute façon (le plafond est réappliqué à l'écriture). */
  const basculerRestriction = useCallback(async (champ, r, regle, posee) => {
    setDivulgBusy(champ); setDivulgMsg('')
    const autres = {}
    for (const x of RESTRICTIONS) {
      if (x.cle === r.cle) continue
      autres[x.body] = !!(regle && regle[x.col])
    }
    const restantes = { ...autres, [r.body]: !posee }
    const aucune = RESTRICTIONS.every((x) => restantes[x.body] !== true)
    const { ok, json } = aucune
      ? await callApi('divulgation_regle_clear', { logementId, champ, portee_type: 'logement' })
      : await callApi('divulgation_regle_set', { logementId, champ, portee_type: 'logement', ...restantes })
    setDivulgBusy(null)
    if (!ok) { setDivulgMsg((json && json.error) || "Le réglage n'a pas pu être enregistré."); return }
    await chargerDivulgation()
  }, [logementId, chargerDivulgation])

  const [vue, setVue] = useState('sait')
  const [feuille, setFeuille] = useState(null) // 'reveler' | 'faq' | 'essai'

  // ── états portés de LogementIATab ─────────────────────────────────
  const [instructions, setInstructions] = useState('')
  const [instrSaving, setInstrSaving] = useState(false)
  const [instrMsg, setInstrMsg] = useState(null)
  const [iaEnabled, setIaEnabled] = useState(null)
  const [iaSaving, setIaSaving] = useState(false)
  const [iaMsg, setIaMsg] = useState(null)
  const [realSendEnabled, setRealSendEnabled] = useState(null)
  const [realSendSaving, setRealSendSaving] = useState(false)
  const [realSendMsg, setRealSendMsg] = useState(null)
  const [masterSendAllowed, setMasterSendAllowed] = useState(null)
  const [confirmRealSend, setConfirmRealSend] = useState(false)
  const [faqs, setFaqs] = useState([])
  const [faqMsg, setFaqMsg] = useState(null)
  const [corrections, setCorrections] = useState(null)
  const [context, setContext] = useState(null)
  const [vision, setVision] = useState(null)
  const [ctxMsg, setCtxMsg] = useState(null)
  const [ctxBusy, setCtxBusy] = useState(false)
  const [sbCaution, setSbCaution] = useState(false)
  const [sbSejour, setSbSejour] = useState(false)
  // 3e état (décision Dimitri 2026-08-28) — défaut CONFIRMÉ, comme avant.
  const [sbConfirme, setSbConfirme] = useState(true)
  const [sbLivretToken, setSbLivretToken] = useState('')
  const [convo, setConvo] = useState([])
  const [convoMsg, setConvoMsg] = useState('')
  const [convoBusy, setConvoBusy] = useState(false)
  const [convoErr, setConvoErr] = useState(null)
  const contextRef = useRef(null)
  contextRef.current = context

  const loadAll = useCallback(async () => {
    if (!logementId) return
    try {
      const [instrRes, faqRes, cfgRes, corrRes, rsRes, masterRes] = await Promise.all([
        callApi('inbox_ia_instructions_get', { logement_id: logementId }),
        callApi('inbox_ia_faq_list', { logement_id: logementId }),
        callApi('inbox_ia_config_get', { logement_id: logementId }),
        callApi('inbox_ia_corrections_list', { logement_id: logementId }),
        callApi('inbox_real_send_config_get', { logement_id: logementId }),
        callApi('inbox_messages_dry_run_state', {}),
      ])
      if (instrRes.ok) setInstructions(instrRes.json.instructions?.content || '')
      if (faqRes.ok) setFaqs(faqRes.json.faqs || [])
      if (cfgRes.ok) setIaEnabled(cfgRes.json.config?.ia_enabled === true)
      if (corrRes.ok) setCorrections(corrRes.json.corrections || [])
      else setCorrections([])
      if (rsRes.ok) setRealSendEnabled(rsRes.json.config?.real_send_enabled === true)
      else setRealSendEnabled(false)
      if (masterRes.ok) setMasterSendAllowed(masterRes.json.masterSendAllowed === true)
      else setMasterSendAllowed(false)
    } catch (e) {
      setInstrMsg(`⚠️ ${e.message}`)
    }
  }, [logementId])

  useEffect(() => {
    setVue('sait'); setFeuille(null); setContext(null); setVision(null)
    setConvo([]); setConvoMsg(''); setConvoErr(null)
    setSbCaution(false); setSbSejour(false); setSbConfirme(true)
    setSbLivretToken('')
    loadAll()
  }, [loadAll])

  const loadContext = useCallback(async () => {
    setCtxMsg(null); setCtxBusy(true)
    try {
      const res = await callApi('inbox_ia_context', {
        sandbox: { logement_id: logementId, caution_recue: sbCaution,
          sejour_paye: sbSejour, sejour_confirme: sbConfirme,
          livret_token: sbLivretToken.trim() || undefined } })
      if (res.ok) {
        setContext(res.json.context)
        setVision(res.json.vision || null)
      } else setCtxMsg(`⚠️ ${res.json.error || 'Échec'}`)
    } catch (e) { setCtxMsg(`⚠️ ${e.message}`) } finally { setCtxBusy(false) }
  }, [logementId, sbCaution, sbSejour, sbConfirme, sbLivretToken])

  // Recharge auto quand un réglage du banc change (si déjà chargé).
  useEffect(() => {
    if (contextRef.current) loadContext()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sbCaution, sbSejour, sbConfirme, sbLivretToken])

  async function saveIaEligibility(next) {
    setIaSaving(true); setIaMsg(null)
    const prev = iaEnabled
    setIaEnabled(next)
    try {
      const res = await callApi('inbox_ia_config_save', {
        logement_id: logementId, ia_enabled: next })
      if (res.ok) setIaEnabled(res.json.config?.ia_enabled === true)
      else { setIaEnabled(prev); setIaMsg(`⚠️ ${res.json.error || 'Échec'}`) }
    } catch (e) { setIaEnabled(prev); setIaMsg(`⚠️ ${e.message}`) } finally { setIaSaving(false) }
  }

  async function commitRealSend(next) {
    setRealSendSaving(true); setRealSendMsg(null)
    try {
      const res = await callApi('inbox_real_send_config_save', {
        logement_id: logementId, real_send_enabled: next,
        ...(next ? { confirm: 'ENVOI_REEL' } : {}) })
      if (res.ok) setRealSendEnabled(res.json.config?.real_send_enabled === true)
      else setRealSendMsg(`⚠️ ${res.json.error || 'Échec'}`)
    } catch (e) { setRealSendMsg(`⚠️ ${e.message}`) } finally { setRealSendSaving(false) }
  }

  async function sendTurn() {
    const msg = convoMsg.trim()
    if (!msg || convoBusy) return
    setConvoBusy(true); setConvoErr(null)
    const fakeThread = convo.map((t) => ({
      direction: t.role === 'fbz' ? 'outbound' : 'inbound',
      sender_name: t.role === 'fbz' ? 'FBZ' : 'Voyageur',
      body: t.text,
    }))
    const withUser = [...convo, { role: 'voyageur', text: msg }]
    setConvo(withUser); setConvoMsg('')
    try {
      const res = await callApi('inbox_suggest_reply', {
        sandbox: { logement_id: logementId, fake_message: msg,
          fake_thread: fakeThread, caution_recue: sbCaution,
          sejour_paye: sbSejour, sejour_confirme: sbConfirme,
          livret_token: sbLivretToken.trim() || undefined } })
      if (res.ok) {
        const escalated = !!res.json.escalated
        setConvo([...withUser, {
          role: 'fbz', text: res.json.draft || '',
          escalation: escalated ? { reasonCode: res.json.reason_code || null } : null,
          diag: res.json._diag || null,
        }])
      } else setConvoErr(`⚠️ ${res.json.error || 'Échec'}`)
    } catch (e) { setConvoErr(`⚠️ ${e.message}`) } finally { setConvoBusy(false) }
  }

  // ── les cartes « il connaît » (vision serveur + dérivés locaux) ───
  // Le COMPTEUR ne porte que la connaissance du logement (8 entrées, comme le
  // proto) — les notes et les questions sont « à lui apprendre », le
  // sous-titre les porte, elles ne comptent pas (constat Dimitri 2026-08-28 :
  // « sur 10 » mentait).
  const cartesSait = [
    ...(Array.isArray(vision) ? vision.map((v) => ({
      lb: v.label || v.titre || '—', s: v.reason || v.raison || '',
      etat: v.ok === true ? 'connu' : (v.conditionnel ? 'condition' : 'non'),
    })) : [
      { lb: 'Le logement', s: 'son nom, son adresse, sa capacité', etat: context ? 'connu' : 'inconnu' },
      { lb: 'Les horaires', s: 'arrivée et départ du contrat', etat: context ? 'connu' : 'inconnu' },
      { lb: 'Le livret', s: 'tout ce que le voyageur y trouve', etat: context ? 'connu' : 'inconnu' },
      { lb: 'Les services', s: 'ce qui est proposé ici, et à quel prix', etat: context ? 'connu' : 'inconnu' },
      { lb: 'Les lieux alentour', s: 'commerces, transports, urgences', etat: context ? 'connu' : 'inconnu' },
      { lb: 'La réservation en cours', s: "dates, source, état du paiement", etat: context ? 'connu' : 'inconnu' },
      { lb: "Les codes d'accès", s: 'porte, boîte à clés, interphone', etat: 'condition' },
      { lb: 'Le mot de passe wifi', s: '', etat: 'condition' },
    ]),
  ]
  const cartesApprendre = [
    { lb: 'Vos notes internes', s: 'ce que vous lui écrivez',
      etat: (instructions || '').trim() ? 'connu' : 'gris' },
    { lb: 'Les questions apprises', s: 'les réponses que vous avez fixées',
      etat: faqs.length > 0 ? 'connu' : 'gris' },
  ]
  const toutesCartes = [...cartesSait, ...cartesApprendre]
  const nbConnues = cartesSait.filter((x) => x.etat === 'connu').length

  const tagPour = (etat) => {
    if (etat === 'connu') return <span className="tag t-v">connu</span>
    if (etat === 'condition') return <span className="tag" style={{ background: 'var(--ambre-bg)', color: 'var(--ambre-tx)' }}>sous condition</span>
    if (etat === 'gris') return <span className="tag" style={{ background: '#eef2f6', color: 'var(--gris)' }}>à renseigner</span>
    return <span className="tag" style={{ background: '#eef2f6', color: 'var(--gris)' }}>charger le contexte</span>
  }

  // ── le banc d'essai (colonne droite, toujours visible) ────────────
  const bancEssai = (
    <div>
      <p className="inter" style={{ marginTop: '0' }}>Essayer une question</p>
      <div className="carte" style={{ margin: '0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
          marginBottom: '9px' }}>
          <span className="tag t-v" data-testid="savoir-banc-badge">Rien ne part d'ici</span>
          <span style={{ fontSize: '10px', color: 'var(--gris-d)' }}>le même
            chemin qu'en vrai</span>
        </div>
        <p style={{ fontSize: '11px', color: 'var(--gris)', margin: '0 0 6px' }}>
          Où en est le séjour</p>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap',
          marginBottom: '9px' }}>
          {[{ lb: 'Caution reçue', on: sbCaution, set: setSbCaution, id: 'caution' },
            { lb: 'Séjour payé', on: sbSejour, set: setSbSejour, id: 'paye' },
            { lb: 'Séjour confirmé', on: sbConfirme, set: setSbConfirme, id: 'confirme' }].map((b) => (
            <button key={b.id} onClick={() => b.set(!b.on)}
              data-testid={`savoir-banc-${b.id}`} aria-pressed={b.on}
              style={b.on
                ? { minHeight: '32px', padding: '0 11px', borderRadius: '9px',
                  fontSize: '10.5px', background: 'var(--vert-bg)',
                  border: '1px solid var(--vert-bd)', color: 'var(--vert-tx)',
                  fontWeight: 600, cursor: 'pointer' }
                : { minHeight: '32px', padding: '0 11px', borderRadius: '9px',
                  fontSize: '10.5px', background: '#fff', border: '1px solid #d5e3ee',
                  color: 'var(--gris)', cursor: 'pointer' }}>{b.lb}</button>
          ))}
          <input value={sbLivretToken}
            onChange={(e) => setSbLivretToken(e.target.value)}
            placeholder="token livret simulé"
            data-testid="savoir-banc-token"
            title="Chaîne littérale jamais persistée — prouve le lien /livret/r/<token>"
            style={{ minHeight: '32px', padding: '0 10px', borderRadius: '9px',
              border: '1px solid #d5e3ee', fontSize: '10.5px', width: '130px',
              outline: 'none' }} />
        </div>
        {context && (
          <p style={{ fontSize: '10px', color: 'var(--gris-d)', margin: '0 0 8px' }}
            data-testid="savoir-etat-reel">
            {context.access_locked ? '🔒 accès verrouillé' : '🔓 accès ouvert'} —
            l'état réel calculé par le même code qu'en production.</p>
        )}
        <div data-testid="savoir-convo">
          {convo.map((t, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '7px',
                background: t.role === 'fbz' ? 'var(--ciel-p)' : '#eef2f6',
                color: 'var(--navy)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: '10px',
                flexShrink: 0 }}>{t.role === 'fbz' ? '🤖' : 'V'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: '11px', color: 'var(--navy-d)',
                  margin: 0, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                  {t.text || (t.escalation ? '—' : '(vide)')}</p>
                {t.escalation && (
                  <p style={{ fontSize: '10px', color: 'var(--ambre-tx)',
                    margin: '3px 0 0' }}>⚠ Escaladé — {reasonLabelFR(t.escalation.reasonCode)}</p>
                )}
                {t.diag && (
                  <>
                    <p style={{ fontSize: '9.5px', color: 'var(--gris-d)',
                      margin: '3px 0 0' }}
                      data-testid="savoir-diag-clair">
                      {diagToSentence(t.diag, !!t.escalation)} (modèle : {modelLabelFR(t.diag.model_used)})</p>
                    <details style={{ marginTop: '3px' }}>
                      <summary style={{ fontSize: '9.5px', color: 'var(--gris-d)',
                        cursor: 'pointer' }}>Détail technique</summary>
                      <p style={{ fontSize: '9px', color: 'var(--gris-d)',
                        margin: '3px 0 0', lineHeight: 1.6 }}>
                        intent {String(t.diag.intent ?? '—')} · conf {String(t.diag.conf ?? t.diag.confidence ?? '—')} ·
                        humain {String(t.diag.requires_human ?? '—')} ·
                        itinéraire {String(t.diag.itinerary_handled ?? '—')} ·
                        sensible {String(t.diag.sensitive_override ?? '—')} ·
                        fil vu {String(t.diag.thread_len ?? '—')}</p>
                    </details>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
        <textarea value={convoMsg} rows={2} data-testid="savoir-convo-msg"
          onChange={(e) => setConvoMsg(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') sendTurn()
          }}
          placeholder="Bonjour, quel est le code de la porte ?"
          style={{ ...inpSavoir, resize: 'vertical', marginBottom: '7px' }} />
        <div className="act" style={{ marginTop: 0 }}>
          <button className="bt bt-p" data-testid="savoir-convo-envoyer"
            disabled={convoBusy || !convoMsg.trim()} onClick={sendTurn}>
            {convoBusy ? 'Il réfléchit…' : 'Envoyer dans le fil'}</button>
          {convo.length > 0 && (
            <button className="bt bt-s" data-testid="savoir-convo-reset"
              onClick={() => { setConvo([]); setConvoMsg(''); setConvoErr(null) }}>
              Réinitialiser le fil</button>
          )}
        </div>
        {convoErr && (
          <p style={{ ...noteStyle, color: '#8a3b28' }}>{convoErr}</p>
        )}
        <p className="note">Basculez « séjour payé » : la réponse change, et le
          code sort. Le banc marche même copilote désactivé — c'est voulu.</p>
      </div>
    </div>
  )

  return (
    <>
      <div className="info">L'opérateur voit toute l'application. Mais pour
        répondre à un voyageur, <b>il vient demander ici</b> — parce que c'est
        cette couche qui connaît le logement et les règles de divulgation.</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '11px',
        background: '#fff', borderRadius: '14px', padding: '11px 13px',
        marginBottom: '12px',
        boxShadow: '0 1px 3px rgba(35,50,64,.08),0 0 0 1px rgba(35,50,64,.05)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '4px',
          flexShrink: 0 }}>
          <b style={{ fontSize: '19px', color: 'var(--navy-d)',
            lineHeight: 1 }}>{nbConnues}</b>
          <span style={{ fontSize: '11px',
            color: 'var(--gris)' }}>sur {cartesSait.length}</span>
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: '12.5px', fontWeight: 600,
            color: 'var(--navy-d)' }}>Il connaît ce logement</span>
          <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--gris)',
            marginTop: '1px' }}>
            {cartesApprendre.filter((x) => x.etat === 'gris').length > 0
              ? `${cartesApprendre.filter((x) => x.etat === 'gris').length} chose${cartesApprendre.filter((x) => x.etat === 'gris').length > 1 ? 's' : ''} reste${cartesApprendre.filter((x) => x.etat === 'gris').length > 1 ? 'nt' : ''} à lui apprendre — vos notes et vos questions`
              : 'notes et questions renseignées'}</span>
        </span>
      </div>

      <div className="cols2">
        <div>
          <div style={{ display: 'flex', gap: '3px', background: '#eef2f6',
            borderRadius: '10px', padding: '3px', marginBottom: '12px',
            overflowX: 'auto', scrollbarWidth: 'none' }}>
            {[{ id: 'sait', lb: "Ce qu'il sait" },
              { id: 'regles', lb: 'Quand il le dit' },
              { id: 'apprendre', lb: 'Lui apprendre' }].map((v) => (
              <button key={v.id} onClick={() => setVue(v.id)}
                data-testid={`savoir-vue-${v.id}`}
                style={vue === v.id
                  ? { flex: '0 0 auto', minHeight: '38px', padding: '0 12px',
                    borderRadius: '8px', fontSize: '11.5px', whiteSpace: 'nowrap',
                    background: '#fff', color: 'var(--navy-d)', fontWeight: 600,
                    boxShadow: '0 1px 3px rgba(35,50,64,.12)', border: 'none',
                    cursor: 'pointer' }
                  : { flex: '0 0 auto', minHeight: '38px', padding: '0 12px',
                    borderRadius: '8px', fontSize: '11.5px', whiteSpace: 'nowrap',
                    color: 'var(--gris)', background: 'transparent', border: 'none',
                    cursor: 'pointer' }}>{v.lb}</button>
            ))}
          </div>

          {/* ═══ Ce qu'il sait ════════════════════════════════════ */}
          {vue === 'sait' && (
            <>
              {toutesCartes.map((x) => (
                <div className="carte" key={x.lb} style={{ margin: '0 0 7px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '12px',
                        fontWeight: 600, color: 'var(--navy-d)' }}>{x.lb}</span>
                      {x.s && (
                        <span style={{ display: 'block', fontSize: '10px',
                          color: 'var(--gris)', marginTop: '1px' }}>{x.s}</span>
                      )}
                    </span>
                    {tagPour(x.etat)}
                  </div>
                </div>
              ))}
              {ctxMsg && (
                <div className="info" style={{ borderColor: '#e8c4b8',
                  background: '#fbeee9', color: '#8a3b28' }}>{ctxMsg}</div>
              )}
              <div className="act">
                <button className="bt bt-s" data-testid="savoir-recharger"
                  disabled={ctxBusy} onClick={loadContext}>
                  {ctxBusy ? 'Chargement…' : (context ? 'Recharger ce qu\'il voit' : 'Charger ce qu\'il voit')}</button>
                <button className="bt bt-s" data-testid="savoir-reveler"
                  disabled={!context}
                  title={context ? 'Vue de contrôle des valeurs protégées'
                    : "Charger le contexte d'abord"}
                  onClick={() => setFeuille('reveler')}>Vérifier les valeurs protégées</button>
              </div>
              <p className="note">Rechargez après avoir modifié le livret ou les
                services, pour voir ce qu'il en perçoit maintenant.</p>
            </>
          )}

          {/* ═══ Quand il le dit (avec le bloc Pilotage — décision 4) ═ */}
          {vue === 'regles' && (
            <>
              <p className="inter" style={{ marginTop: 0 }}>Le pilotage</p>
              <div className="carte" style={{ margin: '0 0 9px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '12px',
                      fontWeight: 600, color: 'var(--navy-d)' }}>Le copilote sur
                      ce logement</span>
                    <span style={{ display: 'block', fontSize: '10px',
                      color: 'var(--gris)', marginTop: '1px' }}>
                      {iaEnabled === null ? 'chargement…'
                        : iaEnabled
                          ? 'il prépare des brouillons pour les réservations de ce logement'
                          : 'il ne prépare AUCUN brouillon — le banc d’essai reste ouvert'}</span>
                  </span>
                  <ToggleRond on={iaEnabled === true}
                    disabled={iaEnabled === null || iaSaving}
                    testId="savoir-eligibilite"
                    ariaLabel="Éligibilité du copilote sur ce logement"
                    onClick={() => saveIaEligibility(!(iaEnabled === true))} />
                </div>
                {iaMsg && <p style={{ ...noteStyle, color: '#8a3b28' }}>{iaMsg}</p>}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px',
                  paddingTop: '9px', marginTop: '9px',
                  borderTop: '1px solid var(--bord)' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '12px',
                      fontWeight: 600, color: 'var(--navy-d)' }}>L'envoi réel</span>
                    <span style={{ display: 'block', fontSize: '10px',
                      marginTop: '1px',
                      color: realSendEnabled && masterSendAllowed ? 'var(--terra-tx)'
                        : realSendEnabled ? 'var(--ambre-tx)' : 'var(--gris)' }}
                      data-testid="savoir-envoi-etat">
                      {realSendEnabled === null ? 'chargement…'
                        : realSendEnabled
                          ? (masterSendAllowed
                            ? 'ACTIF — un brouillon validé peut partir'
                            : 'éligible, mais le verrou de déploiement retient tout')
                          : 'désactivé — rien ne part jamais de ce logement'}</span>
                  </span>
                  <ToggleRond on={realSendEnabled === true}
                    disabled={realSendEnabled === null || realSendSaving}
                    testId="savoir-envoi-reel"
                    ariaLabel="Envoi réel du copilote sur ce logement"
                    onClick={() => {
                      if (realSendEnabled) commitRealSend(false)
                      else setConfirmRealSend(true)
                    }} />
                </div>
                {realSendMsg && <p style={{ ...noteStyle, color: '#8a3b28' }}>{realSendMsg}</p>}
                <p className="note">Deux verrous distincts. Le second (déploiement,
                  MESSAGES_DRY_RUN) ne se lève JAMAIS depuis cet écran.</p>
              </div>

              <div className="info">C'est ici qu'est le garde-fou. L'opérateur
                peut demander n'importe quoi — <b>ces règles décident de ce qui
                sort</b>, et à quel moment.</div>
              {divulg?.lecture_en_panne && (
                <div className="dang" data-testid="divulg-panne">Les règles de ce
                  logement <b>n'ont pas pu être lues</b>. Le moteur applique le
                  socle seul — le plus restrictif. Ce n'est pas « aucune règle ».</div>
              )}
              {CHAMPS_DIVULGATION.map((c) => {
                const p = divulg?.plafond?.[c.champ]
                const regle = (divulg?.regles || []).find((r) => r.champ === c.champ)
                const enPlus = regle ? exigencesEnPlus(p, regle) : []
                return (
                  <div className="carte" key={c.champ} style={{ margin: '0 0 7px' }}
                    data-testid={`divulg-${c.champ}`}>
                    <span style={{ display: 'block', fontSize: '12px', fontWeight: 600,
                      color: 'var(--navy-d)' }}>{c.lb}</span>
                    <span style={{ display: 'block', fontSize: '10.5px',
                      color: 'var(--gris)', marginTop: '2px' }}>
                      {p ? phraseSocle(p) : '—'}
                      {enPlus.length > 0 && (
                        <b style={{ color: '#8a3b28' }}> · restreint ici : {enPlus.join(' et ')}</b>
                      )}
                    </span>
                    {divulg && !divulg.lecture_en_panne && (
                      <div style={{ marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {RESTRICTIONS.filter((r) => p && !p[r.cle]).map((r) => {
                          const posee = !!(regle && regle[r.col])
                          return (
                            <button key={r.cle} type="button"
                              data-testid={`divulg-${c.champ}-${r.cle}`}
                              disabled={divulgBusy === c.champ}
                              onClick={() => basculerRestriction(c.champ, r, regle, posee)}
                              style={{ ...btnRestriction, ...(posee ? btnRestrictionOn : null) }}>
                              {posee ? `✓ exige ${r.lb}` : `exiger ${r.lb}`}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
              {divulgMsg && <p style={{ ...noteStyle, color: '#8a3b28' }}>{divulgMsg}</p>}
              <div className="dang">Un voyageur qui demande son code avant
                d'avoir payé reçoit une réponse qui <b>dit pourquoi</b>, pas un
                refus sec — et jamais le code.</div>
              <p className="note">Le socle ci-dessus vient <b>du moteur</b>, pas
                d'une liste recopiée. Ce que vous réglez ici ne peut que
                <b> restreindre davantage</b> : aucun réglage ne peut ouvrir un
                code plus tôt que le socle. Portée : ce logement.</p>
            </>
          )}

          {/* ═══ Lui apprendre ════════════════════════════════════ */}
          {vue === 'apprendre' && (
            <>
              <p className="inter" style={{ marginTop: 0 }}>Vos notes</p>
              <div className="carte" style={{ margin: '0 0 9px' }}>
                <textarea value={instructions} rows={5}
                  data-testid="savoir-notes"
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Ce que l'opérateur doit savoir sur ce logement et qui n'est écrit nulle part ailleurs…"
                  style={{ ...inpSavoir, resize: 'vertical' }} />
                <p className="note">Par exemple : « la porte du bas colle, il faut
                  pousser en tournant » ou « ne jamais proposer le parking, il est
                  réservé aux résidents ».</p>
                <div className="act">
                  <button className="bt bt-s" data-testid="savoir-notes-enregistrer"
                    disabled={instrSaving}
                    onClick={async () => {
                      setInstrSaving(true); setInstrMsg(null)
                      try {
                        const res = await callApi('inbox_ia_instructions_save', {
                          logement_id: logementId, content: instructions })
                        setInstrMsg(res.ok ? '✅ Enregistré' : `⚠️ ${res.json.error || 'Échec'}`)
                      } catch (e) { setInstrMsg(`⚠️ ${e.message}`) } finally { setInstrSaving(false) }
                    }}>{instrSaving ? 'Enregistrement…' : 'Enregistrer'}</button>
                </div>
                {instrMsg && <p style={noteStyle}>{instrMsg}</p>}
              </div>

              <p className="inter">Les questions qui reviennent</p>
              {faqs.length === 0 ? (
                <div className="vide">
                  <div className="ic">?</div>
                  <p>Aucune question fixée pour ce logement</p>
                  <small>Une question fixée ici l'emporte sur ce que l'opérateur
                    déduirait seul</small>
                  <button className="rv" data-testid="savoir-faq-ajouter"
                    onClick={() => setFeuille('faq')}>＋ Ajouter une question</button>
                </div>
              ) : (
                <>
                  {faqs.map((f) => (
                    <div className="carte" key={f.id} style={{ margin: '0 0 7px' }}
                      data-testid="savoir-faq-ligne">
                      <div style={{ display: 'flex', alignItems: 'flex-start',
                        gap: '10px' }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: '12px',
                            fontWeight: 600,
                            color: 'var(--navy-d)' }}>{f.question}</span>
                          <span style={{ display: 'block', fontSize: '10.5px',
                            color: 'var(--gris)', marginTop: '2px',
                            lineHeight: 1.5 }}>{f.answer}</span>
                        </span>
                        <button onClick={() => {
                          callApi('inbox_ia_faq_delete', {
                            logement_id: logementId, id: f.id }).then((res) => {
                            if (res.ok) loadAll()
                            else setFaqMsg(`⚠️ ${res.json.error || 'Échec'}`)
                          })
                        }} aria-label="Supprimer la question"
                          style={{ background: 'none', border: 'none',
                            cursor: 'pointer', color: '#b8412b', fontSize: '13px',
                            flexShrink: 0 }}>🗑</button>
                      </div>
                    </div>
                  ))}
                  <div className="act">
                    <button className="bt bt-s" data-testid="savoir-faq-ajouter"
                      onClick={() => setFeuille('faq')}>＋ Une question</button>
                  </div>
                </>
              )}
              {faqMsg && <p style={{ ...noteStyle, color: '#8a3b28' }}>{faqMsg}</p>}

              <p className="inter">Ce qu'il retient de vos corrections</p>
              <div className="carte" style={{ margin: '0' }}>
                <p style={{ fontSize: '11.5px', color: 'var(--gris)', margin: 0,
                  lineHeight: 1.6 }}>Quand vous modifiez une réponse avant de
                  l'envoyer, il garde la paire — ce qu'il proposait, ce que vous
                  avez écrit. Sans nom, sans montant.</p>
                {corrections === null && (
                  <p className="note">Chargement…</p>
                )}
                {Array.isArray(corrections) && corrections.length === 0 && (
                  <p className="note">Rien pour l'instant. La première correction
                    apparaîtra ici.</p>
                )}
                {Array.isArray(corrections) && corrections.map((corr, i) => (
                  <div key={corr.id || i} style={{ paddingTop: '8px', marginTop: '8px',
                    borderTop: '1px solid var(--bord)' }}
                    data-testid="savoir-correction">
                    <p style={{ fontSize: '9.5px', color: 'var(--gris-d)',
                      margin: '0 0 3px' }}>
                      {(corr.created_at || '').slice(0, 10)}{corr.lang ? ` · ${String(corr.lang).toUpperCase()}` : ''}</p>
                    <p style={{ fontSize: '10.5px', color: 'var(--gris)',
                      margin: '0 0 3px', lineHeight: 1.5 }}>{corr.draft_anon}</p>
                    <p style={{ fontSize: '10.5px', color: 'var(--navy-d)',
                      margin: 0, lineHeight: 1.5 }}>→ {corr.sent_anon}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="surOrdi">
          <div style={{ position: 'sticky', top: '14px' }}>{bancEssai}</div>
        </div>
      </div>
      <div className="act surTel" style={{ marginTop: '12px' }}>
        <button className="bt bt-p" onClick={() => setFeuille('essai')}>Essayer
          une question</button>
      </div>

      {/* ═══ les feuilles ══════════════════════════════════════════ */}
      {feuille === 'reveler' && context && (
        <Feuille titre="Les valeurs protégées" testId="feuille-reveler"
          onFermer={() => setFeuille(null)}
          enfants={(
            <>
              <div className="info">Vue de contrôle. Cela <b>n'ouvre rien au
                voyageur</b> — les règles de divulgation continuent de
                s'appliquer.</div>
              <div className="carte" style={{ margin: 0 }}>
                {Object.entries(context.access || {}).map(([cle, valeur], i) => (
                  <div key={cle} style={{ display: 'flex', alignItems: 'center',
                    gap: '9px', padding: '8px 0',
                    borderTop: i > 0 ? '1px solid var(--bord)' : 'none' }}>
                    <span style={{ flex: 1, fontSize: '12px',
                      color: 'var(--gris)' }}>{LIBELLES_ACCES[cle] || cle}</span>
                    <b style={{ fontSize: '12px',
                      color: 'var(--navy-d)' }}>{String(valeur ?? '—')}</b>
                  </div>
                ))}
                {(!context.access || Object.keys(context.access).length === 0) && (
                  <p style={{ fontSize: '11.5px', color: 'var(--gris)',
                    margin: 0 }}>Aucune valeur protégée dans le contexte chargé
                    (accès verrouillé, ou rien de renseigné).</p>
                )}
              </div>
              <details style={{ marginTop: '9px' }}>
                <summary style={{ fontSize: '10.5px', color: 'var(--gris)',
                  cursor: 'pointer' }}>Données brutes (JSON)</summary>
                <pre data-testid="savoir-context-json"
                  style={{ fontSize: '9px', background: '#f6f8fa',
                    borderRadius: '9px', padding: '9px', overflow: 'auto',
                    maxHeight: '300px' }}>{JSON.stringify(context, null, 2)}</pre>
              </details>
              <div className="act">
                <button className="bt bt-s" onClick={() => setFeuille(null)}>Fermer</button>
              </div>
            </>
          )} />
      )}
      {feuille === 'faq' && (
        <FeuilleFaqSavoir logementId={logementId}
          onCree={() => { loadAll(); setFeuille(null) }}
          onFermer={() => setFeuille(null)} />
      )}
      {feuille === 'essai' && (
        <Feuille titre="Essayer une question" testId="feuille-essai"
          onFermer={() => setFeuille(null)} enfants={bancEssai} />
      )}
      {confirmRealSend && (
        <Feuille titre="Activer l'envoi réel ?" testId="feuille-envoi-reel"
          onFermer={() => setConfirmRealSend(false)}
          enfants={(
            <>
              {masterSendAllowed ? (
                <div className="dang">Le verrou de déploiement est <b>OUVERT</b> :
                  après confirmation, un message validé peut PARTIR immédiatement
                  de ce logement.</div>
              ) : (
                <div className="warn">Ce logement devient éligible, mais{' '}
                  <b>RIEN ne partira</b> tant que le second verrou (déploiement,
                  MESSAGES_DRY_RUN) n'est pas levé — et il ne se lève jamais
                  d'ici.</div>
              )}
              <div className="act">
                <button className="bt bt-p" data-testid="envoi-reel-confirmer"
                  onClick={async () => {
                    setConfirmRealSend(false)
                    await commitRealSend(true)
                  }}>Confirmer l'activation</button>
                <button className="bt bt-s"
                  onClick={() => setConfirmRealSend(false)}>Annuler</button>
              </div>
            </>
          )} />
      )}
    </>
  )
}

/* ── la feuille « Ajouter une question » (gardes serveur scrubbées) ── */
function FeuilleFaqSavoir({ logementId, onCree, onFermer }) {
  const [q, setQ] = useState('')
  const [a, setA] = useState('')
  const [msg, setMsg] = useState(null)
  const [enCours, setEnCours] = useState(false)
  return (
    <Feuille titre="La question" testId="feuille-faq-savoir" onFermer={onFermer}
      enfants={(
        <>
          <p style={{ fontSize: '11px', color: 'var(--gris)', margin: '0 0 5px' }}>
            La question, comme le voyageur la pose</p>
          <input value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="faq-question" style={{ ...inpSavoir, minHeight: '38px',
              marginBottom: '9px' }} />
          <p style={{ fontSize: '11px', color: 'var(--gris)', margin: '0 0 5px' }}>
            La réponse que vous fixez</p>
          <textarea value={a} rows={3} onChange={(e) => setA(e.target.value)}
            data-testid="faq-reponse"
            style={{ ...inpSavoir, resize: 'vertical', marginBottom: '9px' }} />
          {msg && <p style={{ ...noteStyle, color: '#8a3b28',
            margin: '0 0 8px' }}>{msg}</p>}
          <p className="note">Jamais de code, d'adresse ni de wifi ici : une
            question sert tous les voyageurs, le serveur retire ces valeurs.</p>
          <div className="act">
            <button className="bt bt-p" data-testid="faq-creer" disabled={enCours}
              onClick={async () => {
                if (!q.trim() || !a.trim()) { setMsg('⚠️ Question et réponse requises'); return }
                setEnCours(true); setMsg(null)
                try {
                  const res = await callApi('inbox_ia_faq_create', {
                    logement_id: logementId, question: q.trim(), answer: a.trim() })
                  if (res.ok) onCree()
                  else { setMsg(`⚠️ ${res.json.error || 'Échec'}`); setEnCours(false) }
                } catch (e) { setMsg(`⚠️ ${e.message}`); setEnCours(false) }
              }}>Ajouter</button>
            <button className="bt bt-s" onClick={onFermer}>Annuler</button>
          </div>
        </>
      )} />
  )
}
